import { defineStore } from 'pinia';
import { ChangeVehicleDetailsService as _changeVehicleDetails } from '@/services/store/change-vehicle-details.service';
import { computed, ref } from 'vue';
import {
  ApplyRulesResponseOfChangeVehicleViewModel,
  ChangeVehicleUpdateModel,
  ChangeVehicleViewModel,
  InsurancePolicy,
  VehicleCoverage,
} from '@/interfaces';
import { QuotePremiumDetails } from '@/interfaces/entities/premium-details.interface';
import {
  IJeopardy,
  INameCodeWithDefault,
  IReviewSummary,
  IServiceResponse,
  IServiceResponseError,
  ISitecoreReviewSummaryItem,
  RACQAddressResult,
} from '@/interfaces/entities';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import {
  ProcessPaymentRequest,
  QuoteSession,
  QuoteSessionService as _quoteSessionService,
} from '@/services/store/quote-session.service';
import { CarService } from '@/services/store/car.service';
import { format } from 'date-fns';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { useReferenceDataIQMotorStore } from '../reference-data/reference-data-iq-motor.state';
import { FormEvents } from '@/enums/form-events.enum';
import { IAnalyticsPaymentDetail, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { QuoteVehicleValuationMethods, VehicleValueType } from '@/enums/quote-vehicle-valudation-methods.enum';
import { useReferenceDataStore } from '../reference-data/reference-data.state';
import { MotorOptionalBenefitsSitecoreItem } from '@/enums/motor-optional-benefits.enum';
import { mapTransmission } from '@/mappers/find-car-mapper';
import { toCurrency, toNumber, toBool } from '@/utils/convert';
import { InsuranceQuoteType, QuoteTypeCodes, getInsuranceTypeDisplayName } from '@/interfaces';
import { mapAddress } from '@/utils/review-summary-mapping-util';
import { MotorOptionalBenefits } from '@/enums/motor-optional-benefits.enum';

export const useChangeVehicleDetailsStore = defineStore('ChangeVehicleDetails', () => {
  const refMotorStore = useReferenceDataIQMotorStore();
  const refStoreCommon = useReferenceDataStore();
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quoteSessionId = ref<string>();
  const currentPolicy = ref<InsurancePolicy>();
  const model = ref<ChangeVehicleUpdateModel>();
  const quotePremiumDetails = ref<QuotePremiumDetails>();
  const jeopardies = ref<IJeopardy[]>([]);
  const errors = ref<IServiceResponseError[]>([]);
  const businessRuleResponse = ref<ApplyRulesResponseOfChangeVehicleViewModel>();
  const quoteSession = ref<IServiceResponse<QuoteSession>>();
  const excessData = ref<INameCodeWithDefault[]>();
  const paymentReceiptNumber = ref<string>();
  const analyticsStore = useAnalyticsStore();
  const currentCoverage = computed(
    () => currentPolicy.value?.coverages.filter((x) => x.id == quoteSession.value?.data?.coverageIds[0])[0]
  );

  // ------------------------------------------------------------- //
  // Define Functions
  // ------------------------------------------------------------- //

  /**
   * Get Policy
   */
  async function getPolicy() {
    const params = new URLSearchParams(location.search);
    quoteSessionId.value = params.get('quoteSessionId') ?? '';
    if (!quoteSessionId.value) throw JP_GENERIC_CODE;
    const response = await _changeVehicleDetails.getPolicy(quoteSessionId.value);
    errors.value = response.errors;
    if (response.success && response.data) {
      currentPolicy.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
  }

  /**
   * Apply Business Rules
   */
  async function applyBusinessRules(requestQuote?: boolean, bindChange?: boolean) {
    const response = await _changeVehicleDetails.applyBusinessRules({
      quoteSessionId: quoteSessionId.value,
      model: model.value,
      requestQuote: requestQuote,
      bindChange: bindChange,
    });
    errors.value = response.errors;
    if (response.success && response.data) {
      jeopardies.value = response.data.jeopardies;
      quotePremiumDetails.value = response.data?.viewModel?.quotePremiumDetails;
      businessRuleResponse.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    if (jeopardies.value && jeopardies.value.length > 0) {
      throw jeopardies.value[0].code;
    }
  }

  const getQuoteSession = async () => {
    const response = await _quoteSessionService.getQuoteSession(quoteSessionId.value ?? '');
    errors.value = response.errors;
    if (response.success && response.data) {
      quoteSession.value = response;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    if (jeopardies.value && jeopardies.value.length > 0) {
      throw jeopardies.value[0].code;
    }
  };

  const getMotorExcess = async (quoteType: string, startDate?: string): Promise<INameCodeWithDefault[]> => {
    const excess = await CarService.getMotorExcess(quoteType, startDate);
    excessData.value = excess;
    return excess;
  };

  const filterItems = (itemsWrapper: any, viewModel?: ChangeVehicleViewModel): any => {
    const { items } = itemsWrapper;

    const filteredItems = items.filter((item: any) => {
      if (item.name === MotorOptionalBenefitsSitecoreItem.NCDProtection && !viewModel?.isAllowedToSelectNcdp) {
        return false;
      }
      if (
        item.name === MotorOptionalBenefitsSitecoreItem.Windscreen &&
        !viewModel?.isAllowedToSelectWindscreenReplacement
      ) {
        return false;
      }
      if (item.name === MotorOptionalBenefitsSitecoreItem.HireCar && !viewModel?.isAllowedToSelectCarHire) {
        return false;
      }
      return true;
    });

    return {
      ...itemsWrapper,
      items: filteredItems,
    };
  };
  /**
   * Generate a review summary model for review and confirmation steps
   */
  async function getReviewSummaryModel(summaryFields: any): Promise<IReviewSummary[]> {
    const summaryFieldsResult: IReviewSummary[] = [];

    // The first IReviewSummary item is the changeEffectiveDate section
    const changeEffectiveDate = format(model.value?.changeEffectiveDate ?? '', STANDARD_DATE_FORMAT);
    const insuranceType = await refStoreCommon.getInsuranceProductName(model.value?.coverageCode ?? '');
    const insuredType = VehicleValueType[model.value?.vehicleValueType ?? QuoteVehicleValuationMethods.MARKET_VALUE];
    const policyDetailsObj = {
      insuranceType,
      changeEffectiveDate,
      sumInsured: `${insuredType}${insuredType == VehicleValueType.agreedValue ? ` - ${toCurrency(model.value?.agreedVehicleValue ?? model.value?.vehicleSumInsuredValue ?? 0, false)}` : ''}`,
      basicExcess: model.value?.standardExcess,
      additionalCarHire: '',
      freeWindScreen: '',
      noClaimDiscountProtection: '',
    };
    policyDetailsObj.additionalCarHire =
      businessRuleResponse.value?.viewModel.eligibleForFreeCarHire || model.value?.includeCarHire ? 'Included' : 'None';

    policyDetailsObj.freeWindScreen =
      businessRuleResponse.value?.viewModel.eligibleForFreeWindscreenReplacement ||
      model.value?.includeWindscreenReplacement
        ? 'Included'
        : 'None';

    policyDetailsObj.noClaimDiscountProtection =
      businessRuleResponse.value?.viewModel.eligibleForFreeNcdp || model.value?.includeNcdp ? 'Included' : 'None';

    summaryFields[0] = filterItems(summaryFields[0], businessRuleResponse.value?.viewModel);

    summaryFieldsResult.push(replaceBookmarks(summaryFields[0], policyDetailsObj));

    // The second IReviewSummary item from sitecore is the vehicle summary section. We want to duplicate it for each coverage on the policy.
    const accessories = [] as string[];
    if (model.value?.vehicleAccessories?.length) {
      const accessoriesAndMod = await refMotorStore.getAccessoriesAndModification();

      model.value?.vehicleAccessories.forEach((value: string) => {
        accessories.push(accessoriesAndMod.filter((x) => x.value == value)[0].name);
      });
    }
    const summary = {
      makeAndModel: `${model.value?.glassesGuideVehicleDetails?.make.description} ${model.value?.glassesGuideVehicleDetails?.model.description} ${model.value?.vehicleRegistration}`,
      yearBody: `${model.value?.glassesGuideVehicleDetails?.manufacturingYear} ${model.value?.glassesGuideVehicleDetails?.variant.description} ${model.value?.glassesGuideVehicleDetails?.option?.series} ${model.value?.glassesGuideVehicleDetails?.option?.doors} door ${mapTransmission(model.value?.glassesGuideVehicleDetails?.option.transmission)}`,
      garagingType: model.value?.overnightParkingLocationCode,
      suburbAndPostCode: `${model.value?.overnightParkingAddress}`,
      finance: model.value?.financeCode == 'NOFINANCE' ? 'No finance' : `${model.value?.financeProvider}`,
      use: model.value?.registeredInBusinessNameOrUsedForBusinessPurposes ? 'Business' : 'Private',
      accessoriesAndModifications: accessories?.length ? accessories.join(', ') : 'None',
    };
    let summaryItem = {
      formContentId: summaryFields[1].formContentId,
      summaryTitle: summaryFields[1].summaryTitle,
      icon: summaryFields[1].icon,
      items: summaryFields[1].items,
    };
    summaryItem = replaceBookmarks(summaryItem, summary);
    summaryFieldsResult.push(summaryItem);

    return summaryFieldsResult.map((field: any) => ({
      ...field,
      items: field.items.map((item: ISitecoreReviewSummaryItem) => {
        return {
          ...item,
          ...item.fields,
          editGoToStep: item.fields.editStep?.value,
          showEdit: item.fields.editStep?.value ?? false,
        };
      }),
    }));
  }
  const pushAnalytics = async (
    options: {
      event: FormEvents;
      amount?: number;
      payment?: Partial<IAnalyticsPaymentDetail>;
    },
    dictionary: Record<string, string>
  ) => {
    try {
      const firstVehicle =
        businessRuleResponse.value?.viewModel?.newVehicle ?? (currentCoverage.value as VehicleCoverage)?.vehicle;
      let motorExcess: string = '';
      if (!model.value?.standardExcess) {
        const motorExcessResponse = await getMotorExcess(
          currentCoverage?.value?.coverageCode ?? '',
          new Date().toString()
        );
        motorExcess = motorExcessResponse.filter(
          (x: any) => x.code == (currentCoverage.value as VehicleCoverage)?.basicExcessCode
        )[0].name;
      }

      const analyticsModel: IFormAnalytics = {
        event: options.event,
        form: {
          productName: getInsuranceTypeDisplayName(
            dictionary,
            model.value?.coverageCode ?? currentCoverage.value?.coverageCode ?? ''
          ),
          termEndDate: new Date(currentPolicy?.value?.termEnd ?? '')?.toDateString(),
          detail: {
            makeName: firstVehicle?.make,
            modelName: firstVehicle?.model,
            vehicleYear: firstVehicle?.year,
            excess: model.value?.standardExcess ? toNumber(model.value?.standardExcess ?? '') : toNumber(motorExcess),
          },
        },
      };
      if (options.amount) {
        analyticsModel.form.amount = options.amount;
      }
      if (options.payment) {
        analyticsModel.payment = options.payment;
      }
      if (currentPolicy?.value?.state.isInRenewal) {
        analyticsModel.form.field = 'In renewal';
      }
      analyticsStore.pushAnalytics(analyticsModel);
    } catch (e) {
      console.error('Error pushing to analytics', e);
    }
  };
  /**
   * Process Payment
   */
  async function processPayment(data: ProcessPaymentRequest) {
    const response = await _quoteSessionService.processPayment(
      currentPolicy.value?.policyNumber ?? '',
      quoteSessionId.value ?? '',
      data
    );
    errors.value = response.errors;
    paymentReceiptNumber.value = response.data?.receiptNumber;
    if (response.errors && response.errors.length > 0) {
      throw response.errors[0].message ?? JP_GENERIC_CODE;
    }
    return response;
  }

  const mapModelChanges = (formData: Record<string, any>, typeChanged: boolean) => {
    model.value!.agreedVehicleValue =
      formData.vehicleValueType == QuoteVehicleValuationMethods.AGREED_VALUE ? formData.vehicleAgreedValue : undefined;
    if (
      model.value!.coverageCode == QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability] &&
      formData.vehicleValueType == QuoteVehicleValuationMethods.AGREED_VALUE
    ) {
      model.value!.vehicleSumInsuredValue = formData.vehicleAgreedValue;
    }
    if (formData.vehicleValueType == QuoteVehicleValuationMethods.AGREED_VALUE && typeChanged) {
      formData.vehicleAgreedValue = model.value!.glassesGuideVehicleDetails?.marketValue;
    }

    model.value!.includeCarHire = (formData.optionalBenefitOptions as string[])?.includes('hirecar');
    model.value!.includeWindscreenReplacement = (formData.optionalBenefitOptions as string[])?.includes('windscreen');
    model.value!.includeNcdp = (formData.optionalBenefitOptions as string[])?.includes('noClaimDiscountProtection');
    const excess = excessData.value?.find(
      (item: INameCodeWithDefault) => item.code.toString() == formData.excessAmount?.toString()
    );
    if (excess) {
      model.value!.standardExcessCode = excess.code;
      model.value!.standardExcess = excess.name;
    }
  };
  const convertToInsuranceQuoteType = (value: string): InsuranceQuoteType => {
    return InsuranceQuoteType[value as keyof typeof InsuranceQuoteType];
  };
  const mapCarDetails = async (formData?: Record<string, any>, vehicleFoundUsingRego?: boolean) => {
    model.value!.overnightParkingLocationCode = formData?.parkingLocation;
    model.value!.overnightParkingLocationPostcode = formData?.addressLookup?.postcode;
    model.value!.overnightParkingAddress = mapAddress(formData?.addressLookup as RACQAddressResult);
    model.value!.overnightParkingLocationSuburb = (formData?.addressLookup?.suburb as string)?.toUpperCase();
    model.value!.overnightParkingLocationState = formData?.addressLookup.state ?? '';

    model.value!.isVehicleRegistered = vehicleFoundUsingRego ? true : toBool(formData?.isCarRegistered);
    model.value!.financeCode = toBool(formData?.financeOnCar) ? formData?.financeType : 'NOFINANCE';
    model.value!.financeProvider =
      toBool(formData?.financeOnCar) && formData?.financeProvider ? formData?.financeProvider : '';

    model.value!.registeredInBusinessNameOrUsedForBusinessPurposes = (
      formData as any
    )?.registeredInBusinessNameOrUsedForBusinessPurposes;
    model.value!.vehicleUsedForUnacceptableBusinessUse = (formData as any)?.vehicleUsedForUnacceptableBusinessUse;

    if (formData?.rego) {
      //only if vehicle was not found using rego lookup
      model.value!.vehicleRegistration = model.value!.glassesGuideVehicleDetails!.registrationNumber = formData?.rego;
    }
    model.value!.unrepairedCollisionOrHailDamage = toBool(formData?.hasDamage);
    if (formData?.hasAccessories) {
      model.value!.vehicleAccessories = formData?.accessories;
    }

    if (formData?.coverageType) {
      model.value!.coverageType = formData?.coverageType;
      model.value!.coverageCode = QuoteTypeCodes[convertToInsuranceQuoteType(formData?.coverageType ?? '')];

      const excesses = await getMotorExcess(
        model.value?.coverageCode ?? '',
        model.value?.changeEffectiveDate.toString()
      );
      model.value!.standardExcessCode = excesses.find((item: INameCodeWithDefault) => item.isDefault)?.code;
      model.value!.standardExcess = excesses.find((item: INameCodeWithDefault) => item.isDefault)?.name;
    }
    if (model.value!.coverageCode == 'MF') {
      model.value!.vehicleSumInsuredValue = model.value!.glassesGuideVehicleDetails?.marketValue;
    }
    if (model.value!.coverageCode == 'MT') {
      model.value!.vehicleHasModifications = toBool(formData?.hasBeenModified);
    }
  };

  const mapIncludedBenefits = (): string[] => {
    const optionalBenefitOptions = [];
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeCarHire) {
      optionalBenefitOptions.push(MotorOptionalBenefits.HireCar);
    }
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeNcdp) {
      optionalBenefitOptions.push(MotorOptionalBenefits.NCDProtection);
    }
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeWindscreenReplacement) {
      optionalBenefitOptions.push(MotorOptionalBenefits.Windscreen);
    }
    return optionalBenefitOptions;
  };

  const deleteQuoteSession = async () => {
    try {
      await _quoteSessionService.deleteQuoteSession(quoteSessionId.value ?? '');
    } catch (e) {
      //ignore if delete quote session doesn't work
      console.error('Error delete quote session', e);
    }
  };
  function $reset() {}

  return {
    getPolicy,
    applyBusinessRules,
    processPayment,
    quoteSessionId,
    currentPolicy,
    quotePremiumDetails,
    pushAnalytics,
    jeopardies,
    model,
    paymentReceiptNumber,
    getQuoteSession,
    currentCoverage,
    businessRuleResponse,
    getMotorExcess,
    excessData,
    errors,
    getReviewSummaryModel,
    mapModelChanges,
    mapCarDetails,
    mapIncludedBenefits,
    convertToInsuranceQuoteType,
    deleteQuoteSession,
  };
});
